// Turns the room's 10 Hz remote pointer stream back into per-frame motion.
//
// Cursor messages are what the collab Worker's daily request budget is spent
// on (Cloudflare bills an inbound WebSocket message at 20:1), so
// `CURSOR_SYNC_TIMEOUT` sends one every 100 ms instead of every 33 ms. Drawn
// as they arrive, that is ten visible steps a second, which reads as stutter.
// This module keeps the network rate and puts the smoothness back on the
// receiving client, where it is free.

import type { SocketId } from "@excalidraw/excalidraw/types";

import { CURSOR_INTERPOLATION_DURATION_MS } from "../app_constants";

/** A pointer position in scene coordinates: the only part of a remote pointer this module animates. */
export interface CursorPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * The animation clock. Injected so tests can step frames by hand; the real one
 * is `requestAnimationFrame`.
 */
export interface FrameScheduler {
  request: (onFrame: (timestampMs: number) => void) => number;
  cancel: (handle: number) => void;
}

/**
 * Where "is this tab in the background" comes from. Injected for the same
 * reason as the scheduler: a test cannot put a real tab in the background.
 */
export interface VisibilitySource {
  isHidden: () => boolean;
  subscribe: (onChange: () => void) => () => void;
}

/** Drives the animation from the browser's display refresh. */
export const createAnimationFrameScheduler = (): FrameScheduler => ({
  request: (onFrame) => window.requestAnimationFrame(onFrame),
  cancel: (handle) => window.cancelAnimationFrame(handle),
});

/** Reads background-tab state from the real document. */
export const createDocumentVisibilitySource = (): VisibilitySource => ({
  isHidden: () => document.hidden,
  subscribe: (onChange) => {
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  },
});

export interface RemoteCursorInterpolatorOptions {
  /**
   * Called once per animation frame with every peer whose drawn position
   * moved. One call carries all of them, so the editor pays for the frame, not
   * for the number of peers in it.
   */
  onFrame: (positions: ReadonlyMap<SocketId, CursorPoint>) => void;
  /** Defaults to `CURSOR_INTERPOLATION_DURATION_MS`; `0` turns interpolation off. */
  durationMs?: number;
  scheduler?: FrameScheduler;
  visibility?: VisibilitySource;
}

/**
 * One peer's cursor: where it is drawn, where the newest message says it
 * really is, and how far along the ramp between the two we are.
 *
 * The ramp is linear and always restarts from the currently drawn position, so
 * a message that arrives early, late, or not at all only changes the speed of
 * the next leg, never the continuity of the path.
 */
class CursorTrack {
  private readonly durationMs: number;
  private from: CursorPoint;
  private to: CursorPoint;
  private elapsedMs: number;

  /** The position the editor should draw for this peer right now. */
  current: CursorPoint;

  /**
   * Starts already settled on `point`, so a peer's first cursor appears where
   * the peer is instead of gliding in from wherever the previous owner of that
   * socket id happened to be.
   */
  constructor(point: CursorPoint, durationMs: number) {
    this.durationMs = durationMs;
    this.from = point;
    this.to = point;
    this.current = point;
    this.elapsedMs = durationMs;
  }

  /** True while the drawn position has not caught up with the newest received one. */
  get isAnimating(): boolean {
    return this.elapsedMs < this.durationMs;
  }

  /** Places the cursor on `point` with no animation at all. */
  jumpTo(point: CursorPoint): void {
    this.from = point;
    this.to = point;
    this.current = point;
    this.elapsedMs = this.durationMs;
  }

  /** Finishes the ramp immediately, landing on the newest received position. */
  settle(): void {
    this.jumpTo(this.to);
  }

  /** Aims the ramp at a newly received position, restarting it from wherever the cursor is drawn now. */
  retarget(point: CursorPoint): void {
    if (point.x === this.to.x && point.y === this.to.y) {
      // Same position again. Restarting the ramp here would re-spread whatever
      // distance is left over another full window, which only adds lag.
      return;
    }
    if (
      this.durationMs <= 0 ||
      (point.x === this.current.x && point.y === this.current.y)
    ) {
      // Nothing to animate: either interpolation is switched off, or the peer
      // came back to where its cursor already is. Animating a zero-length ramp
      // would spin the loop for a whole window without moving a pixel.
      this.jumpTo(point);
      return;
    }
    this.from = this.current;
    this.to = point;
    this.elapsedMs = 0;
  }

  /** Advances the ramp by one frame, reporting whether the drawn position moved. */
  advance(deltaMs: number): boolean {
    if (!this.isAnimating) {
      return false;
    }
    this.elapsedMs = Math.min(this.elapsedMs + deltaMs, this.durationMs);
    const progress = this.elapsedMs / this.durationMs;
    const next: CursorPoint = {
      x: this.from.x + (this.to.x - this.from.x) * progress,
      y: this.from.y + (this.to.y - this.from.y) * progress,
    };
    const moved = next.x !== this.current.x || next.y !== this.current.y;
    this.current = next;
    return moved;
  }
}

/**
 * Owns one animation loop for every remote cursor in the room.
 *
 * The loop runs only while at least one cursor is still travelling, and never
 * while the document is hidden, so an idle or backgrounded tab costs nothing.
 */
export class RemoteCursorInterpolator {
  private readonly tracks = new Map<SocketId, CursorTrack>();
  private readonly onFrame: RemoteCursorInterpolatorOptions["onFrame"];
  private readonly durationMs: number;
  private readonly scheduler: FrameScheduler;
  private readonly visibility: VisibilitySource;
  private readonly unsubscribeVisibility: () => void;
  private frameHandle: number | null = null;
  private lastFrameMs: number | null = null;

  /** Wires up the loop and starts listening for the tab going to the background. */
  constructor(options: RemoteCursorInterpolatorOptions) {
    this.onFrame = options.onFrame;
    this.durationMs = Math.max(
      0,
      options.durationMs ?? CURSOR_INTERPOLATION_DURATION_MS,
    );
    this.scheduler = options.scheduler ?? createAnimationFrameScheduler();
    this.visibility = options.visibility ?? createDocumentVisibilitySource();
    this.unsubscribeVisibility = this.visibility.subscribe(
      this.handleVisibilityChange,
    );
  }

  /** True while a frame is scheduled, i.e. while a cursor is still travelling. */
  get isAnimating(): boolean {
    return this.frameHandle !== null;
  }

  /**
   * Records the position a peer just reported and returns the position that
   * should be drawn for it this instant, which is normally an earlier one.
   * Duplicated and out-of-order messages are handled by the only rule this
   * needs: the newest message wins and the cursor travels there from wherever
   * it currently is.
   */
  track(socketId: SocketId, point: CursorPoint): CursorPoint {
    const existing = this.tracks.get(socketId);
    if (existing === undefined) {
      const track = new CursorTrack(point, this.durationMs);
      this.tracks.set(socketId, track);
      return track.current;
    }
    if (this.visibility.isHidden()) {
      // Nobody is watching, so spend nothing and stay correct: jump straight
      // to the reported position.
      existing.jumpTo(point);
      return existing.current;
    }
    existing.retarget(point);
    if (existing.isAnimating) {
      this.start();
    }
    return existing.current;
  }

  /**
   * Forgets every peer that is not in `socketIds` any more. Called whenever the
   * room membership changes, so a peer that left or reconnected under a new
   * socket id cannot leave a cursor behind mid-ramp.
   */
  retain(socketIds: Iterable<SocketId>): void {
    const kept = new Set(socketIds);
    for (const socketId of [...this.tracks.keys()]) {
      if (!kept.has(socketId)) {
        this.tracks.delete(socketId);
      }
    }
    if (!this.hasAnimatingTrack()) {
      this.stop();
    }
  }

  /** Drops every cursor and stops the loop; used when the room goes away. */
  reset(): void {
    this.tracks.clear();
    this.stop();
  }

  /** Releases the visibility listener. Nothing may call the instance afterwards. */
  destroy(): void {
    this.reset();
    this.unsubscribeVisibility();
  }

  /** Settles every cursor and stops the loop once the tab goes to the background. */
  private handleVisibilityChange = (): void => {
    if (!this.visibility.isHidden()) {
      return;
    }
    this.stop();
    const settled = new Map<SocketId, CursorPoint>();
    for (const [socketId, track] of this.tracks) {
      if (!track.isAnimating) {
        continue;
      }
      track.settle();
      settled.set(socketId, track.current);
    }
    if (settled.size > 0) {
      this.onFrame(settled);
    }
  };

  /** Starts the loop unless it is already running or the tab is hidden. */
  private start(): void {
    if (this.frameHandle !== null || this.visibility.isHidden()) {
      return;
    }
    // The first frame of a burst has no previous timestamp to measure against,
    // so it advances nothing and only establishes the clock.
    this.lastFrameMs = null;
    this.schedule();
  }

  /** Idempotent, so a re-entrant call from `onFrame` cannot leave two frames pending. */
  private schedule(): void {
    if (this.frameHandle !== null) {
      return;
    }
    this.frameHandle = this.scheduler.request(this.handleFrame);
  }

  private stop(): void {
    if (this.frameHandle === null) {
      return;
    }
    this.scheduler.cancel(this.frameHandle);
    this.frameHandle = null;
    this.lastFrameMs = null;
  }

  private handleFrame = (timestampMs: number): void => {
    this.frameHandle = null;
    const deltaMs =
      this.lastFrameMs === null ? 0 : timestampMs - this.lastFrameMs;
    this.lastFrameMs = timestampMs;

    const moved = new Map<SocketId, CursorPoint>();
    for (const [socketId, track] of this.tracks) {
      if (track.advance(deltaMs)) {
        moved.set(socketId, track.current);
      }
    }
    if (moved.size > 0) {
      this.onFrame(moved);
    }
    if (this.hasAnimatingTrack() && !this.visibility.isHidden()) {
      this.schedule();
    }
  };

  private hasAnimatingTrack(): boolean {
    for (const track of this.tracks.values()) {
      if (track.isAnimating) {
        return true;
      }
    }
    return false;
  }
}
